import React from 'react';

interface TrackWaveformProps {
  /** Stable identifier (e.g. asset id) used to seed the bar heights so the same
   * asset always renders the same-looking waveform, while different assets look
   * visually distinct. This is a decorative placeholder — it does not analyze
   * the actual audio waveform. */
  seed: string;
  bars?: number;
  height?: number;
  color?: string;
}

// Simple deterministic string -> number hash (djb2-ish), used purely to seed
// the decorative sine/cosine bar heights below.
function seedToNumber(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash;
}

const TrackWaveformBase: React.FC<TrackWaveformProps> = ({
  seed,
  bars = 34,
  height = 24,
  color = '#CBD5E1',
}) => {
  // Offset derived from the seed so different assets produce visually
  // different (but each individually stable) bar patterns.
  const offset = ((seedToNumber(seed) % 1000) / 1000) * Math.PI * 2;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${bars * 5} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      {Array.from({ length: bars }, (_, i) => {
        const bh =
          4 +
          Math.abs(
            Math.sin(i * 0.63 + 1.4 + offset) * (height - 8) +
              Math.cos(i * 0.41 + offset) * 5
          );
        const c = Math.min(bh, height - 2);
        return (
          <rect
            key={i}
            x={i * 5}
            y={(height - c) / 2}
            width={2.6}
            height={c}
            rx={1.3}
            fill={color}
          />
        );
      })}
    </svg>
  );
};

// Only re-render when the seed changes — sibling row state changes (e.g. a
// different row toggling play/pause) shouldn't force this to redraw.
const TrackWaveform = React.memo(
  TrackWaveformBase,
  (prev, next) => prev.seed === next.seed
);

export default TrackWaveform;
